import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import countryDialCodes from '../data/countryDialCodes';
import logo from '../assets/images/logouhk.png';
import { registerUser, loginUser } from '../services/api';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { useAuth } from '../context/AuthContext';
import cguCgvContent from '../data/cguCgvContent';

const getFlagEmoji = (isoCode = '') => {
  if (isoCode.length !== 2) return '🏳️';
  return isoCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
};

const defaultCountry = countryDialCodes.find((country) => country.iso === 'FR') || countryDialCodes[0];
const termsText = cguCgvContent;

const defaultSignupData = {
  civility: 'Mr',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  phoneCountry: defaultCountry.iso,
  phonePrefix: defaultCountry.dialCode,
  phone: '',
  street: '',
  postalCode: '',
  city: '',
  country: 'France'
};

function EyeIcon({ isHidden }) {
  return isHidden ? (
    <svg viewBox="0 0 24 24" className="password-eye-icon" aria-hidden="true">
      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.2 10.2a3.15 3.15 0 0 0 4.47 4.47" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.1 6.2C4 7.8 2.8 9.7 2 11.5c1.6 3.8 5.2 6.5 10 6.5 1.5 0 2.9-.2 4.1-.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.8 6c3.4.2 6.2 2.1 8.2 5.5-.8 1.9-2.2 3.8-4.2 5.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" className="password-eye-icon" aria-hidden="true">
      <path d="M2 12c2.2-4 5.8-6.5 10-6.5s7.8 2.5 10 6.5c-2.2 4-5.8 6.5-10 6.5S4.2 16 2 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

export default function Auth() {
  const [rememberMe, setRememberMe] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [signupErrors, setSignupErrors] = useState({ email: '', password: '', confirm: '', terms: '' });
  const [signupStatus, setSignupStatus] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsContent, setShowTermsContent] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [signupData, setSignupData] = useState(defaultSignupData);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const handleLogin = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const emailValue = (formData.get('email') || '').toString().toLowerCase();
    try {
      await loginUser({
        email: emailValue,
        password: formData.get('password'),
        rememberMe
      });
      await refreshUser();
      navigate('/');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    setSignupStatus('');
    const errors = { email: '', password: '', confirm: '', terms: '' };
    const normalizedEmail = signupData.email.trim().toLowerCase();
    if (signupData.password.length < 6 || !/[!@#$%^&*(),.?":{}|<>+=-]/.test(signupData.password) || !/\d/.test(signupData.password)) {
      errors.password = 'Votre mot de passe doit contenir au minimum 6 caractères, 1 chiffre et 1 caractère spécial.';
    }
    if (signupData.password !== signupData.confirmPassword) {
      errors.confirm = 'Les mots de passe doivent être identiques.';
    }
    if (!termsAccepted) {
      errors.terms = 'Vous devez accepter les mentions légales - CGU - CGV.';
    }
    setSignupErrors(errors);
    if (errors.email || errors.password || errors.confirm || errors.terms) return;
    try {
      const normalizedPhone = [signupData.phonePrefix, signupData.phone].filter(Boolean).join(' ').trim();
      const response = await registerUser({
        civility: signupData.civility,
        firstName: signupData.firstName,
        lastName: signupData.lastName,
        email: normalizedEmail,
        password: signupData.password,
        phone: normalizedPhone,
        street: signupData.street,
        postalCode: signupData.postalCode,
        city: signupData.city,
        country: signupData.country
      });
      if (response.verificationToken) {
        setSignupStatus("Merci ! Un e-mail de vérification vient de vous être envoyé. Cliquez sur le lien reçu pour activer votre compte.");
      } else {
        setSignupStatus('Votre compte est actif. Vous pouvez vous connecter dès maintenant.');
        await refreshUser();
      }
      setSignupData({ ...defaultSignupData });
      setTermsAccepted(false);
      navigate('/');
    } catch (error) {
      if (error.message && error.message.toLowerCase().includes('déjà')) {
        setSignupErrors((prev) => ({ ...prev, email: 'Cette adresse e-mail est déjà utilisée.' }));
      } else {
        alert(error.message);
      }
    }
  };

  return (
    <Fragment>
      <section className="auth-page">
        <div className="auth-modal__panel auth-modal__panel--page">
          <div className="auth-modal__grid">
            <div className="auth-modal__intro">
              {showSignup ? (
                <>
                  <img src={logo} alt="Unisport du Haut-Nkam" className="auth-modal__logo" />
                  <h3>Formulaire d’inscription</h3>
                  <p>Déjà inscrit ? Reprenez la connexion en un clic.</p>
                  <button type="button" className="auth-modal__ghost-btn" onClick={() => setShowSignup(false)}>
                    Se connecter
                  </button>
                </>
              ) : (
                <>
                  <h3>Nouveau chez Unisport ?</h3>
                  <p>Créez votre compte pour profiter des avantages supporters.</p>
                  <button type="button" className="auth-modal__ghost-btn" onClick={() => setShowSignup(true)}>
                    S&apos;inscrire
                  </button>
                </>
              )}
            </div>
            <div className="auth-modal__form-wrapper">
              {!showSignup ? (
                <form key="login-form" className="auth-modal__form" onSubmit={handleLogin}>
                  <h3>Se connecter</h3>
                  <p>Accédez à vos commandes et espaces personnels.</p>
                  <label>
                    Adresse e-mail
                    <input type="email" name="email" required autoComplete="email" />
                  </label>
                  <label>
                    Mot de passe
                    <div className="password-input">
                      <input type={showLoginPassword ? 'text' : 'password'} name="password" required autoComplete="current-password" />
                      <button
                        type="button"
                        className="password-input__toggle"
                        onClick={() => setShowLoginPassword((prev) => !prev)}
                        aria-label={showLoginPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      >
                        <EyeIcon isHidden={!showLoginPassword} />
                      </button>
                    </div>
                  </label>
                  <div className="auth-modal__remember-row">
                    <label className="auth-modal__remember">
                      <input type="checkbox" checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} />
                      Se souvenir de moi
                    </label>
                    <button type="button" className="auth-modal__link auth-modal__link--accent" onClick={() => setForgotPasswordOpen(true)}>
                      Mot de passe oublié ?
                    </button>
                  </div>
                  <button type="submit" className="auth-modal__primary">
                    Se connecter
                  </button>
                </form>
              ) : (
                <form key="signup-form" className="auth-modal__form" onSubmit={handleSignup}>
                  <h3>S&apos;inscrire</h3>
                  <label>
                    <span className="form-label">
                      Civilité <span className="required-marker">*</span>
                    </span>
                    <select value={signupData.civility} onChange={(event) => setSignupData((prev) => ({ ...prev, civility: event.target.value }))}>
                      <option value="Mr">Monsieur</option>
                      <option value="Mme">Madame</option>
                      <option value="Mlle">Mademoiselle</option>
                    </select>
                  </label>
                  <label>
                    <span className="form-label">
                      Nom <span className="required-marker">*</span>
                    </span>
                    <input type="text" value={signupData.lastName} onChange={(event) => setSignupData((prev) => ({ ...prev, lastName: event.target.value }))} required />
                  </label>
                  <label>
                    <span className="form-label">
                      Prénoms <span className="required-marker">*</span>
                    </span>
                    <input type="text" value={signupData.firstName} onChange={(event) => setSignupData((prev) => ({ ...prev, firstName: event.target.value }))} required />
                  </label>
                  <label>
                    <span className="form-label">
                      Adresse e-mail <span className="required-marker">*</span>
                    </span>
                    <input
                      type="email"
                      value={signupData.email}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSignupData((prev) => ({ ...prev, email: value }));
                        if (signupErrors.email) {
                          setSignupErrors((prev) => ({ ...prev, email: '' }));
                        }
                      }}
                      required
                    />
                  </label>
                  {signupErrors.email && <p className="auth-modal__error auth-modal__error--inline">{signupErrors.email}</p>}
                  <label>
                    <span className="form-label">
                      Mot de passe <span className="required-marker">*</span>
                    </span>
                    <div className="password-input">
                      <input type={showSignupPassword ? 'text' : 'password'} value={signupData.password} onChange={(event) => setSignupData((prev) => ({ ...prev, password: event.target.value }))} required />
                      <button
                        type="button"
                        className="password-input__toggle"
                        onClick={() => setShowSignupPassword((prev) => !prev)}
                        aria-label={showSignupPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      >
                        <EyeIcon isHidden={!showSignupPassword} />
                      </button>
                    </div>
                  </label>
                  {signupErrors.password && <p className="auth-modal__error auth-modal__error--inline">{signupErrors.password}</p>}
                  <label>
                    <span className="form-label">
                      Confirmer le mot de passe <span className="required-marker">*</span>
                    </span>
                    <div className="password-input">
                      <input type={showSignupConfirmPassword ? 'text' : 'password'} value={signupData.confirmPassword} onChange={(event) => setSignupData((prev) => ({ ...prev, confirmPassword: event.target.value }))} required />
                      <button
                        type="button"
                        className="password-input__toggle"
                        onClick={() => setShowSignupConfirmPassword((prev) => !prev)}
                        aria-label={showSignupConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      >
                        <EyeIcon isHidden={!showSignupConfirmPassword} />
                      </button>
                    </div>
                  </label>
                  {signupErrors.confirm && <p className="auth-modal__error auth-modal__error--inline">{signupErrors.confirm}</p>}
                  <label>Indicatif téléphonique
                    <select
                      value={signupData.phoneCountry}
                      onChange={(event) => {
                        const nextCountry = countryDialCodes.find((country) => country.iso === event.target.value);
                        if (!nextCountry) return;
                        setSignupData((prev) => ({ ...prev, phoneCountry: nextCountry.iso, phonePrefix: nextCountry.dialCode }));
                      }}
                    >
*** End Patch
                      {countryDialCodes.map((country) => (
                        <option key={country.iso} value={country.iso}>
                          {getFlagEmoji(country.iso)} {country.name} ({country.dialCode})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Numéro de téléphone
                    <input type="tel" value={signupData.phone} onChange={(event) => setSignupData((prev) => ({ ...prev, phone: event.target.value }))} />
                  </label>
                  <label>
                    Nom et numéro de la voie
                    <input type="text" value={signupData.street} onChange={(event) => setSignupData((prev) => ({ ...prev, street: event.target.value }))} />
                  </label>
                  <label>
                    Code postal
                    <input type="text" value={signupData.postalCode} onChange={(event) => setSignupData((prev) => ({ ...prev, postalCode: event.target.value }))} />
                  </label>
                  <label>
                    Ville
                    <input type="text" value={signupData.city} onChange={(event) => setSignupData((prev) => ({ ...prev, city: event.target.value }))} />
                  </label>
                  <label>
                    Pays
                    <input type="text" value={signupData.country} onChange={(event) => setSignupData((prev) => ({ ...prev, country: event.target.value }))} required />
                  </label>
                  <label className="auth-modal__terms">
                    <input type="checkbox" checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} />
                    <span>
                      Accepter les{' '}
                      <button type="button" className="terms-link" onClick={() => setShowTermsContent(true)}>
                        mentions légales - CGU - CGV
                      </button>
                    </span>
                  </label>
                  {signupErrors.terms && <p className="auth-modal__error auth-modal__error--inline">{signupErrors.terms}</p>}
                  <div className="auth-modal__actions">
                    <button type="submit" className="auth-modal__primary auth-modal__primary--full" disabled={!termsAccepted}>
                      Valider l’inscription
                    </button>
                  </div>
                  {signupStatus && <p className="auth-modal__status">{signupStatus}</p>}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      {showTermsContent && (
        <div className="terms-modal" role="dialog" aria-modal="true" aria-label="Mentions légales">
          <div className="terms-modal__content">
            <pre>{termsText}</pre>
            <button type="button" className="terms-modal__close-btn" onClick={() => setShowTermsContent(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}
      {forgotPasswordOpen && <ForgotPasswordModal isOpen={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)} />}
    </Fragment>
  );
}
